import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ptApi, ptError } from "@/pathlync/api";
import { PathLyncShell, StatCard, StatusPill } from "@/pathlync/PathLyncShell";
import { toast } from "sonner";
import { Loader2, Images, FileText } from "lucide-react";

const TABS = [
  ["pool", "Pending requests"],
  ["mine", "My consultations"],
];

const PathologistDashboard = () => {
  const [tab, setTab] = useState("pool");
  const [rows, setRows] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);

  const load = useCallback(async (scope) => {
    setLoading(true);
    try {
      const [list, s] = await Promise.all([
        ptApi.get("/consults", { params: { scope: scope === "pool" ? "pool" : "mine" } }),
        ptApi.get("/stats"),
      ]);
      setRows(list.data);
      setStats(s.data);
    } catch (e) { toast.error(ptError(e)); } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(tab); }, [tab, load]);

  return (
    <PathLyncShell>
      <div data-testid="pathologist-dashboard">
        <h1 className="font-display text-2xl font-bold tracking-tight">Consultation queue</h1>
        <p className="text-sm text-slate-600">
          Requests from laboratories awaiting a remote opinion.
        </p>
      </div>

      <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard label="Pending requests" value={stats.pending ?? 0} testid="stat-pending"
                  loading={loading} />
        <StatCard label="Under my review" value={stats.in_review ?? 0} testid="stat-in-review"
                  tone="text-sky-700" loading={loading} />
        <StatCard label="Reports issued" value={stats.reported ?? 0} testid="stat-issued"
                  tone="text-emerald-700" loading={loading} />
        <StatCard label="Urgent waiting" value={stats.urgent ?? 0} testid="stat-urgent-waiting"
                  tone="text-red-700" loading={loading} />
      </div>

      <div className="mt-8 flex border-b border-slate-200">
        {TABS.map(([key, label]) => (
          <button key={key} onClick={() => setTab(key)} data-testid={`pathologist-tab-${key}`}
                  className={`-mb-px border-b-2 px-4 py-2 text-sm font-semibold transition-colors ${
                    tab === key ? "border-[#1e3a8a] text-[#1e3a8a]"
                      : "border-transparent text-slate-500 hover:text-slate-800"}`}>
            {label}
          </button>
        ))}
      </div>

      {loading && <Loader2 className="mt-8 animate-spin text-slate-400" size={20} />}

      {!loading && rows.length === 0 && (
        <p className="mt-8 border border-dashed border-slate-300 p-12 text-center text-sm text-slate-500"
           data-testid="pathologist-empty">
          {tab === "pool" ? "No consultations are waiting right now."
            : "You have not accepted any consultations yet."}
        </p>
      )}

      <div className="mt-6 space-y-3">
        {rows.map((r) => (
          <Link key={r.id} to={`/pathlync/consults/${r.id}`} data-testid={`consult-row-${r.id}`}
                className="block border border-slate-200 bg-white p-4 transition-colors hover:border-[#1e3a8a]">
            <div className="flex flex-wrap items-center gap-2">
              <span className="font-mono text-sm font-semibold text-[#1e3a8a]">{r.reference}</span>
              <StatusPill status={r.status} />
              {r.priority === "Urgent" && (
                <span className="rounded-full bg-red-100 px-2 py-0.5 text-xs font-semibold text-red-800">
                  Urgent
                </span>
              )}
              <span className="ml-auto inline-flex items-center gap-1 text-xs text-slate-500">
                <Images size={12} /> {r.image_count}
              </span>
            </div>
            <p className="mt-2 text-sm font-semibold text-slate-900">{r.specimen_type}</p>
            <p className="text-sm text-slate-600">
              {r.requester_institution || r.requester_name} · patient ref {r.patient_reference}
              {r.age_sex ? ` · ${r.age_sex}` : ""}
            </p>
            <p className="mt-1 line-clamp-2 text-sm italic text-slate-500">{r.question}</p>
            {r.status === "Reported" && (
              <p className="mt-2 inline-flex items-center gap-1 text-xs font-semibold text-emerald-700">
                <FileText size={12} /> Report issued
              </p>
            )}
          </Link>
        ))}
      </div>
    </PathLyncShell>
  );
};

export default PathologistDashboard;
